/** Reading a circuit's history as credit and as a timeline.
 *
 * `GET /v1/tracks/{id}/history` answers with what the service recorded — the
 * accounts with merged work, the pull requests that carried it, and the
 * uploads still waiting — and a name only for a contributor who asked to be
 * named. Two things on the circuit's page are derived from it, and both are
 * derived here so the rules can be tested without a screen.
 *
 * The credit list names the named and counts the rest. Twelve rows reading
 * "a contributor" are one fact said twelve times, so the anonymous accounts
 * are folded into a single line with their metres — still credited, as a
 * number, which is all they have agreed to be.
 *
 * The timeline is the same history in the order it happened, newest first:
 * what is waiting, then each merge with the surveys it carried. A merge's
 * surveys are in the order they were driven, because "Ana surveyed it on the
 * 1st, Ben on the 3rd, merged on the 4th" is how the sentence reads.
 */

import type {
  TrackContributor,
  TrackHistory,
  TrackHistoryMerge,
  TrackHistoryUpload,
} from '@/lib/types';
import { pullRequestLabel } from '@/components/tracks/list';

/** What stands in for a name nobody has agreed to publish. */
export const ANONYMOUS = 'a contributor';

export function contributorLabel(name: string | null): string {
  return name ?? ANONYMOUS;
}

export interface Credits {
  /** Contributors credited by name, the largest contribution first. */
  named: TrackContributor[];
  /** Everybody else, as a count and what they added between them. */
  anonymous: { accounts: number; uploads: number; metres: number } | null;
}

export function creditsFrom(contributors: readonly TrackContributor[]): Credits {
  const named: TrackContributor[] = [];
  let accounts = 0;
  let uploads = 0;
  let metres = 0;

  for (const contributor of contributors) {
    if (contributor.name !== null) {
      named.push(contributor);
      continue;
    }
    accounts += 1;
    uploads += contributor.uploads;
    metres += contributor.metres;
  }

  named.sort((a, b) => b.metres - a.metres || a.first_at.localeCompare(b.first_at));
  return { named, anonymous: accounts > 0 ? { accounts, uploads, metres } : null };
}

/** Who a merge credits, each name once, in the order they first surveyed.
 *  Anonymous surveys come last as one "a contributor" — two of them may be
 *  the same account or two, and the page does not know and does not guess. */
export function mergeCredit(merge: Pick<TrackHistoryMerge, 'uploads'>): string[] {
  const names: string[] = [];
  let anonymous = false;
  for (const upload of byReceived(merge.uploads)) {
    if (upload.name === null) anonymous = true;
    else if (!names.includes(upload.name)) names.push(upload.name);
  }
  return anonymous ? [...names, ANONYMOUS] : names;
}

export function byReceived(uploads: readonly TrackHistoryUpload[]): TrackHistoryUpload[] {
  return [...uploads].sort((a, b) => a.received_at.localeCompare(b.received_at));
}

export type TimelineEntry =
  | { kind: 'waiting'; at: string; uploads: TrackHistoryUpload[] }
  | {
      kind: 'merged';
      at: string;
      pr_url: string;
      /** `PR #84`, or null for a merge with no pull request behind it. */
      pr_label: string | null;
      metres: number;
      uploads: TrackHistoryUpload[];
    };

/** Newest first: the uploads still waiting, as one entry, then each merge. */
export function timelineFrom(history: TrackHistory): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  if (history.waiting.length > 0) {
    const uploads = byReceived(history.waiting);
    entries.push({
      kind: 'waiting',
      at: uploads[uploads.length - 1]?.received_at ?? '',
      uploads,
    });
  }

  const merges = [...history.merges].sort((a, b) => b.merged_at.localeCompare(a.merged_at));
  for (const merge of merges) {
    entries.push({
      kind: 'merged',
      at: merge.merged_at,
      pr_url: merge.pr_url,
      pr_label: pullRequestLabel(merge.pr_url),
      metres: merge.metres,
      uploads: byReceived(merge.uploads),
    });
  }

  return entries;
}

/** The first survey anybody sent that made it onto the map. */
export function firstSurveyedAt(history: TrackHistory): string | null {
  let first: string | null = null;
  for (const contributor of history.contributors) {
    if (first === null || contributor.first_at < first) first = contributor.first_at;
  }
  return first;
}
